import { type AppointmentChainConfig, parseChainConfigs } from '@/models/appointment-chain-config.ts';
import useSWRImmutable from 'swr/immutable';

import { fetchPublicChainConfigs } from '@/service/Appointments/chain-config-service.ts';

import { InsuranceType } from '@/utils/constants.js';

type InsuranceTypeValue = (typeof InsuranceType)[keyof typeof InsuranceType];

interface ChainConfigsKey {
    key: 'public-chain-configs';
    customerId: string;
    locationKey: string;
    insuranceType?: InsuranceTypeValue;
}

interface UseChainConfigsResult {
    chainConfigs: AppointmentChainConfig[];
    getChainConfigurationsByPractitionerPrismicKey: (prismicKey: string) => AppointmentChainConfig[];
    isLoading: boolean;
    error: unknown;
}

const chainConfigsFetcher = async ({
    customerId,
    locationKey,
    insuranceType,
}: ChainConfigsKey): Promise<AppointmentChainConfig[]> => {
    const body = await fetchPublicChainConfigs(customerId, locationKey, insuranceType);

    // `processAPIWithHeadersForPractitioners` returns the JSON body even on 4xx/5xx:
    // a `200 + { data: [] }` no-match yields `[]`, while an error body lacks the `data` array
    // and throws, surfacing as an SWR `error` rather than crashing.
    if (!Array.isArray(body.data)) {
        throw new TypeError('chain-configs: response body did not contain a data array');
    }

    return parseChainConfigs(body.data);
};

/**
 * Fetches the public chain configs for the current customer/location/insurance.
 *
 * Gated by `enabled` (the ConfigCat flag value): when the flag is off or any required param is missing,
 * the SWR key is `null` so the endpoint is never called. Changing any query param produces a
 * new key, triggering an auto-refetch.
 */
export const useChainConfigs = (
    enabled: boolean,
    customerId: string | undefined,
    locationKey: string | undefined,
    insuranceType?: InsuranceTypeValue
): UseChainConfigsResult => {
    const key: ChainConfigsKey | null =
        enabled && customerId && locationKey
            ? {
                  key: 'public-chain-configs',
                  customerId: customerId,
                  locationKey: locationKey,
                  insuranceType: insuranceType,
              }
            : null;

    const { data: chainConfigs, isLoading, error } = useSWRImmutable(key, chainConfigsFetcher);

    const getChainConfigurationsByPractitionerPrismicKey = (
        prismicKey: string | undefined
    ): AppointmentChainConfig[] => {
        if (!prismicKey) {
            return [];
        }
        const doesChainBelongToProfessional = (config: AppointmentChainConfig): boolean =>
            config.steps.every(
                (step) =>
                    !step.is_main_practitioner ||
                    step.eligible_practitioners.some((practitioner) => practitioner.prismic_key === prismicKey)
            );
        return chainConfigs?.filter(doesChainBelongToProfessional) ?? [];
    };

    return {
        chainConfigs: chainConfigs ?? [],
        getChainConfigurationsByPractitionerPrismicKey: getChainConfigurationsByPractitionerPrismicKey,
        isLoading: isLoading,
        error: error,
    };
};
